import React from "react";

// chakra imports
import {
  Box,
  Flex,
  Drawer,
  DrawerBody,
  Icon,
  useColorModeValue,
  DrawerOverlay,
  useDisclosure,
  DrawerContent,
  DrawerCloseButton,
  IconButton,
  Text,
} from "components-lib";
import Content from "components/sidebar/components/Content";
import {
  renderThumb,
  renderTrack,
  renderView,
} from "components/scrollbar/Scrollbar";
import { Scrollbars } from "react-custom-scrollbars-2";
import PropTypes from "prop-types";

// Assets
import { IoMenuOutline } from "react-icons/io5";
import { FiChevronsLeft } from "react-icons/fi";

function Sidebar(props) {
  const {
    routes,
    sidebarWidth = 280,
    isCollapsed = false,
    onToggleCollapse,
    ...rest
  } = props;

  let variantChange = "0.2s linear";
  let shadow = useColorModeValue(
    "14px 17px 40px 4px rgba(112, 144, 176, 0.08)",
    "unset"
  );
  // Chakra Color Mode
  let sidebarBg = useColorModeValue("white", "navy.800");
  let sidebarMargins = "0px";
  const computedWidth = `${sidebarWidth}px`;

  // SIDEBAR
  return (
    <Box
      display={{ base: "none", xl: "block" }}
      w={computedWidth}
      position="fixed"
      top="0"
      left="0"
      minH="100%"
      role="navigation"
      aria-label="Primary Navigation"
      transform={isCollapsed ? "translateX(-110%)" : "translateX(0)"}
      transition="transform 0.3s ease"
      aria-hidden={isCollapsed}
      zIndex="900"
      data-collapsed={isCollapsed ? "true" : "false"}
      {...rest}
    >
      <Box
        as="nav"
        bg={sidebarBg}
        transition={variantChange}
        w={computedWidth}
        h='100vh'
        m={sidebarMargins}
        minH='100%'
        overflowX='hidden'
        boxShadow={shadow}
        px={2}>
       
        {/* Skip link: visible on keyboard focus */}
        <Box
          as="a"
          href="#main-content"
          position="absolute"
          left="-9999px"
          _focus={{
            position: 'static',
            left: 0, 
            top: 4,
            zIndex: 9999,
            bg: sidebarBg,
            px: 4,
            py: 2,
            borderRadius: 'md',
            boxShadow: '0 2px 6px rgba(0,0,0,0.08)'
          }}>
          Skip to main content
        </Box>

        <Scrollbars
          autoHide
          renderTrackVertical={renderTrack}
          renderThumbVertical={renderThumb}
          renderView={renderView}>
          {typeof onToggleCollapse === "function" && (
            <IconButton
              aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              icon={<FiChevronsLeft />}
              size="sm"
              variant="ghost"
              position="absolute"
              top="14px"
              right={isCollapsed ? "-10px" : "14px"}
              onClick={onToggleCollapse}
              _hover={{ bg: "transparent" }}
              display={{ base: "none", xl: "flex" }}
            />
          )}
          <Content routes={routes} />
        </Scrollbars>
      </Box>
    </Box>
  );
}

// FUNCTIONS
export function SidebarResponsive(props) {
  let sidebarBackgroundColor = useColorModeValue("white", "navy.800");
  let menuColor = useColorModeValue("gray.400", "white");
  // // SIDEBAR
  const { isOpen, onOpen, onClose } = useDisclosure();
  const btnRef = React.useRef();

  const { routes } = props;

  return (
    <Flex display={{ base: "flex", xl: "none" }} alignItems='center'>
      <Box
        as="button"
        ref={btnRef}
        type="button"
        aria-label="Open navigation menu"
        bg="transparent"
        border="none"
        p={2}
        onClick={onOpen}
        cursor="pointer"
        _hover={{ bg: "gray.100" }}
        borderRadius="md">
        <Icon
          as={IoMenuOutline}
          color={menuColor}
          my='auto'
          w='24px'
          h='24px'
          me='10px'
        />
      </Box>
      <Drawer
        isOpen={isOpen}
        onClose={onClose}
        placement={document.documentElement.dir === "rtl" ? "right" : "left"}
        finalFocusRef={btnRef}
        blockScrollOnMount={true}
        preserveScrollBarGap={true}
        size="sm">
        <DrawerContent 
          w='280px' 
          maxW='280px' 
          bg={sidebarBackgroundColor} 
          role="navigation" 
          aria-label="Mobile Navigation"
          zIndex={2001}
          boxShadow="xl"
        >
          <DrawerCloseButton
            zIndex='2002'
            _focus={{ boxShadow: '0 0 0 4px rgba(66,153,225,0.6)' }}
            _hover={{ bg: "gray.200" }}
            aria-label="Close navigation"
          />
          <DrawerBody 
            maxW='280px' 
            px='0' 
            pb='20px' 
            pt='70px'
            overflowY="auto"
            onClick={onClose}
            css={{
              '&::-webkit-scrollbar': {
                width: '4px',
              },
              '&::-webkit-scrollbar-track': {
                width: '6px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'gray',
                borderRadius: '24px',
              },
            }}>
            {routes && routes.length > 0 ? (
              <Content routes={routes} />
            ) : (
              <Box p={4}>
                <Text color="gray.500" textAlign="center">No navigation items available</Text>
              </Box>
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Flex>
  );
}
// PROPS

Sidebar.propTypes = {
  logoText: PropTypes.string,
  routes: PropTypes.arrayOf(PropTypes.object),
  variant: PropTypes.string,
  sidebarWidth: PropTypes.number,
  isCollapsed: PropTypes.bool,
  onToggleCollapse: PropTypes.func,
};

export default Sidebar;
